/**
 * FERP Portal - Safety Badge Component
 *
 * DRR-027: Displays safety level and dual approval status for normalized entities.
 */

import { Badge, Group, Tooltip } from '@mantine/core';
import { IconShieldCheck, IconAlertTriangle } from '@tabler/icons-react';
import { SafetyLevel } from '@/types/graphql-compat';
import type { NormalizedEntity } from '@/types/normalized-entity';
import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// SAFETY LEVEL COLORS
// ============================================================================

/**
 * Map SafetyLevel to Mantine color
 */
export function getSafetyLevelColor(safetyLevel: SafetyLevel): string {
  // Compare by value since SafetyLevel maps to FormSafetyLevel values
  switch (safetyLevel) {
    case SafetyLevel.LIFE_CRITICAL:
      return 'red';
    case SafetyLevel.CRITICAL:
      return 'orange';
    case SafetyLevel.HIGH:
    case SafetyLevel.ELEVATED:
      return 'yellow';
    case SafetyLevel.MEDIUM:
    case SafetyLevel.STANDARD:
    case SafetyLevel.LOW:
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * Get display label for SafetyLevel
 */
export function getSafetyLevelLabel(safetyLevel: SafetyLevel): string {
  switch (safetyLevel) {
    case SafetyLevel.LIFE_CRITICAL:
      return 'Life-Critical';
    case SafetyLevel.CRITICAL:
      return 'Critical';
    case SafetyLevel.HIGH:
    case SafetyLevel.ELEVATED:
      return 'High';
    case SafetyLevel.MEDIUM:
    case SafetyLevel.STANDARD:
      return 'Medium';
    case SafetyLevel.LOW:
      return 'Low';
    default:
      return 'Unknown';
  }
}

// ============================================================================
// STATUS COLORS
// ============================================================================

/**
 * Map UnifiedStatus to Mantine color
 */
export function getStatusColor(status: UnifiedStatus): string {
  switch (status) {
    case UnifiedStatus.COMPLETED:
      return 'green';
    case UnifiedStatus.ACTIVE:
      return 'blue';
    case UnifiedStatus.PENDING:
      return 'gray';
    case UnifiedStatus.BLOCKED:
      return 'orange';
    case UnifiedStatus.FAILED:
      return 'red';
    case UnifiedStatus.CANCELLED:
      return 'gray';
    default:
      return 'gray';
  }
}

// ============================================================================
// SAFETY BADGE COMPONENT
// ============================================================================

interface SafetyBadgeProps {
  /** Normalized entity to display safety info for */
  entity: Pick<NormalizedEntity, 'safetyLevel' | 'requiresDualApproval' | 'isBlockedBySafety'>;
  /** Size of the badge */
  size?: 'xs' | 'sm' | 'md';
  /** Show dual approval indicator */
  showDualApproval?: boolean;
}

/**
 * Displays safety level badge with optional dual approval indicator.
 *
 * @example
 * ```tsx
 * <SafetyBadge entity={normalizedTask} showDualApproval />
 * ```
 */
export function SafetyBadge({ entity, size = 'sm', showDualApproval = true }: SafetyBadgeProps) {
  const color = getSafetyLevelColor(entity.safetyLevel);
  const label = getSafetyLevelLabel(entity.safetyLevel);

  return (
    <Group gap={4}>
      <Tooltip label={`Safety Level: ${label}`}>
        <Badge color={color} size={size} variant="filled">
          {label}
        </Badge>
      </Tooltip>

      {showDualApproval && entity.requiresDualApproval && (
        <Tooltip label="Requires dual approval">
          <Badge
            color="orange"
            size={size}
            variant="outline"
            leftSection={<IconShieldCheck size={10} />}
          >
            2x
          </Badge>
        </Tooltip>
      )}

      {entity.isBlockedBySafety && (
        <Tooltip label="Blocked by safety check">
          <Badge
            color="red"
            size={size}
            variant="outline"
            leftSection={<IconAlertTriangle size={10} />}
          >
            Blocked
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================

interface StatusBadgeProps {
  /** Unified status to display */
  status: UnifiedStatus;
  /** Size of the badge */
  size?: 'xs' | 'sm' | 'md';
}

/**
 * Displays unified status badge.
 */
export function StatusBadge({ status, size = 'sm' }: StatusBadgeProps) {
  const color = getStatusColor(status);

  return (
    <Badge color={color} size={size}>
      {status}
    </Badge>
  );
}

export default SafetyBadge;
